import { type User } from '@kalos/kalos-rpc';
import { addDays, format } from 'date-fns';

import { OPTION_ALL } from '../../../constants';
import { type ReimbursementFilter } from '../../../hooks/react-query/useReimbursementQuery';
import { type ReimbursementSearchFormSchema } from './ReimbursementSearchForm.hooks';

/** The list request for one page of a person's reimbursements. Shared by desktop and phone. */
export const getReimbursementSearchFilter = ({
  page,
  search,
  ownerId,
}: {
  ownerId: User['id'];
  page: number;
  search: Partial<ReimbursementSearchFormSchema>;
}): ReimbursementFilter => {
  const { description, statusId, createdDate } = search;

  let dateRange: undefined | string[] = undefined;
  let dateTarget: undefined | string[] = undefined;

  if (createdDate && createdDate != OPTION_ALL && createdDate != '') {
    dateTarget = ['created_date', 'created_date'];
    dateRange = ['>=', createdDate, '<=', format(addDays(new Date(createdDate), 7), 'yyyy-MM-dd')];
  }

  return {
    description: description ? `%${description}%` : undefined,
    isActive: 1,
    orderDir: 'DESC',
    pageNumber: page,
    userId: ownerId,
    statusId: Number(statusId),
    dateRange,
    dateTarget,
  };
};

/**
 * The signed-in person's roles for a reimbursement log, as the log's user query selects them:
 * managers and payroll see anyone's log, everyone else only their own.
 */
export const getReimbursementViewerRoles = (
  data: User,
  loggedUserId: User['id'],
  reimbursementOwnerId: User['id'],
) => {
  const isManager = !!data.permissionGroups.find((p) => p.name === 'Manager' && p.type === 'role');
  const isPayroll = !!data.permissionGroups.find((p) => p.name === 'Payroll' && p.type === 'role');
  const hasPermission = isManager || isPayroll || loggedUserId == reimbursementOwnerId;
  return { ...data, hasPermission, isManager, isPayroll };
};
